/**
 * One place that turns a configured recording package into the `recordingType` the
 * capture pipeline actually acts on.
 *
 * Every recording stage gates on `userPreferences.recordingType`: Capture only builds a
 * per-shot recorder for `strip_live` / `strip_story`, and Select / Edit only start the
 * framed video for those types. But nothing guaranteed the field was set when a guest
 * bought the recording:
 *
 * - Welcome copied the RAW package type, so a package of type `'recording'` stored
 *   `'recording'` — a value Capture's recorder gate does not accept.
 * - Checkout's init effect mapped it correctly, but only fires while no package has been
 *   selected yet, and computes it from `recordingPackage`, which is `null` until the
 *   checkout config and capture mode have both resolved.
 * - Ticking an optional recording package set `useRecording: true` and nothing else.
 *
 * So a guest could pay for the recording with `useRecording: true` and `recordingType:
 * null`: no recorder was built, the framed video was skipped, and nothing was logged
 * (sessions uqd410 / hz6byb). Ticked-optional recordings delivered 63% of the time
 * against 99% when the package was included by default.
 *
 * Pure and import-free (the type import is erased) so it tests without the renderer.
 */
import type { RecordingType } from '@renderer/types/configState'

/** The types the pipeline branches on. `'recording'` is a legacy package type, not one of these. */
export type EffectiveRecordingType = 'strip_live' | 'strip_story' | 'session_recording'

const RECORDING_PACKAGE_TYPES: readonly RecordingType[] = [
  'recording',
  'strip_live',
  'strip_story',
  'session_recording'
]

/** Whether a checkout package type is one that sells a recording. */
export function isRecordingPackageType(type: unknown): type is RecordingType {
  return typeof type === 'string' && (RECORDING_PACKAGE_TYPES as readonly string[]).includes(type)
}

/**
 * Normalise a package type or a stored `recordingType` to the one the pipeline uses.
 * `'recording'` has always meant live mode (Checkout's own mapping); anything that is not a
 * recording type yields `null`.
 */
export function toRecordingType(type: unknown): EffectiveRecordingType | null {
  if (type === 'recording') return 'strip_live'
  if (type === 'strip_live' || type === 'strip_story' || type === 'session_recording') return type
  return null
}

/** Whether this type produces a framed strip video from per-shot clips (vs one session recording). */
export function isFramedStripRecording(type: unknown): boolean {
  const effective = toRecordingType(type)
  return effective === 'strip_live' || effective === 'strip_story'
}
